// src/components/tasks/TaskItem.tsx
// Reusable task row component with completion animation
import React, { useState, useCallback } from 'react';
import { IonIcon, IonRippleEffect } from '@ionic/react';
import { checkmarkCircle, checkmarkCircleOutline, calendarOutline, star } from 'ionicons/icons';
import { Badge } from '../ui/Badge';
import type { TaskAssignment, Task } from '../../types';
import './tasks.css';

export interface TaskItemProps {
  assignment?: TaskAssignment;
  task?: Task;
  onToggle?: (assignmentId: string, completed: boolean, event: React.MouseEvent) => void;
  onTap?: (assignment: TaskAssignment) => void;
  onClick?: () => void;
  showDueDate?: boolean;
  showPoints?: boolean;
  compact?: boolean;
  disabled?: boolean;
  celebrating?: boolean;
}

// Format due date for display
const formatDueDate = (dateString: string): string => {
  const date = new Date(dateString + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.getTime() === today.getTime()) return 'Today';
  if (date.getTime() === tomorrow.getTime()) return 'Tomorrow';
  return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

export const TaskItem: React.FC<TaskItemProps> = ({
  assignment,
  task: propTask,
  onToggle,
  onTap,
  onClick,
  showDueDate = true,
  showPoints = true,
  compact = false,
  disabled = false,
  celebrating = false,
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  // Get task from assignment or prop
  const task = assignment?.task || propTask;
  const isCompleted = assignment?.is_completed ?? false;

  const handleCheckClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled || !assignment || !onToggle) return;

    if (!isCompleted) {
      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 600);
    }

    onToggle(assignment.id, !isCompleted, e);
  }, [assignment, isCompleted, disabled, onToggle]);

  const handleRowClick = useCallback(() => {
    if (onClick) {
      onClick();
    } else if (assignment && onTap) {
      onTap(assignment);
    }
  }, [assignment, onTap, onClick]);

  const isClickable = !!onClick || !!onTap;

  if (!task) return null;

  return (
    <div
      className={`task-item ${isCompleted ? 'completed' : ''} ${celebrating || isAnimating ? 'celebrating' : ''} ${compact ? 'compact' : ''} ${isClickable ? 'clickable' : ''} ion-activatable`}
      onClick={isClickable ? handleRowClick : undefined}
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
    >
      <IonRippleEffect />

      {/* Task Icon */}
      <span className="task-item__icon">{task.icon || '📋'}</span>

      {/* Task Info */}
      <div className="task-item__content">
        <span className="task-item__name">{task.name}</span>
        {showDueDate && assignment?.due_date && (
          <span className="task-item__meta">
            <IonIcon icon={calendarOutline} />
            {formatDueDate(assignment.due_date)}
            {assignment.due_time && ` at ${assignment.due_time}`}
          </span>
        )}
      </div>

      {/* Points Badge */}
      {showPoints && task.points_value > 0 && (
        <Badge
          variant={isCompleted ? 'success' : 'cta'}
          size="sm"
        >
          <IonIcon icon={star} style={{ marginRight: 2 }} />
          {task.points_value}
        </Badge>
      )}

      {/* Completion Button */}
      {assignment && onToggle && (
        <button
          className={`task-item__check-btn ${isCompleted ? 'completed' : ''}`}
          onClick={handleCheckClick}
          disabled={disabled}
          aria-label={isCompleted ? 'Mark as incomplete' : 'Mark as complete'}
        >
          <IonIcon
            icon={isCompleted ? checkmarkCircle : checkmarkCircleOutline}
            className="task-item__check-icon"
          />
        </button>
      )}
    </div>
  );
};

TaskItem.displayName = 'TaskItem';

export default TaskItem;
